import client from "./client";

export type PlanStatus = "draft" | "active" | "closed";
export type HireStatus = "planned" | "approved" | "hired" | "cancelled";

export interface PlannedHire {
  id: string;
  plan_id: string;
  job_title: string;
  department: string | null;
  target_start_date: string | null;
  salary_estimate: number | null;
  currency: string;
  justification: string | null;
  status: HireStatus;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface PlannedHireInput {
  job_title: string;
  department?: string | null;
  target_start_date?: string | null;
  salary_estimate?: number | null;
  currency?: string;
  justification?: string | null;
  status?: HireStatus;
}

export interface PlannedHireUpdate {
  job_title?: string;
  department?: string | null;
  target_start_date?: string | null;
  salary_estimate?: number | null;
  currency?: string;
  justification?: string | null;
  status?: HireStatus;
}

export interface HeadcountPlan {
  id: string;
  title: string;
  fiscal_year: number;
  status: PlanStatus;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  hires: PlannedHire[];
  planned_count: number;
  approved_count: number;
  estimated_annual_cost: number;
}

export interface HeadcountPlanInput {
  title: string;
  fiscal_year: number;
  notes?: string | null;
  status?: PlanStatus;
}

export interface DepartmentHeadcount {
  department_id: string | null;
  department_name: string | null;
  employee_count: number;
}

export interface EmploymentTypeStat {
  employment_type: string;
  employee_count: number;
}

export interface HeadcountSnapshot {
  total_employees: number;
  by_department: DepartmentHeadcount[];
  by_employment_type: EmploymentTypeStat[];
  avg_tenure_years: number;
  growth_rate_12m: number;
  turnover_rate_12m: number;
  hires_last_12m: number;
  departures_last_12m: number;
  estimated_monthly_payroll: number;
  estimated_annual_payroll: number;
  cost_per_employee_annual: number;
  currency: string;
}

export interface ImpactProjection {
  current_headcount: number;
  new_headcount: number;
  headcount_growth_pct: number;
  current_monthly_payroll: number;
  new_monthly_payroll: number;
  monthly_payroll_delta: number;
  current_annual_payroll: number;
  new_annual_payroll: number;
  annual_payroll_delta: number;
  current_cost_per_employee: number;
  new_cost_per_employee: number;
  cost_per_employee_delta: number;
  currency: string;
  by_department: DepartmentHeadcount[];
  ai_summary: string | null;
  caveats: string | null;
}

export interface ScenarioHire {
  job_title: string;
  department?: string | null;
  salary_estimate?: number | null;
  count?: number;
}

export interface ScenarioDeparture {
  department?: string | null;
  job_title?: string | null;
  count?: number;
}

export interface ScenarioRequest {
  hires: ScenarioHire[];
  departures: ScenarioDeparture[];
  notes?: string | null;
  currency?: string;
}

export interface ScenarioResponse {
  current_headcount: number;
  new_headcount: number;
  headcount_growth_pct: number;
  current_monthly_payroll: number;
  new_monthly_payroll: number;
  monthly_payroll_delta: number;
  new_annual_payroll: number;
  annual_payroll_delta: number;
  new_cost_per_employee: number;
  by_department: DepartmentHeadcount[];
  currency: string;
  ai_narrative: string | null;
  risks: string[];
  opportunities: string[];
}

export async function fetchCurrent(): Promise<HeadcountSnapshot> {
  const res = await client.get("/workforce/current");
  return res.data;
}

export async function listPlans(): Promise<HeadcountPlan[]> {
  const res = await client.get("/workforce/plans");
  return res.data;
}

export async function readPlan(id: string): Promise<HeadcountPlan> {
  const res = await client.get(`/workforce/plans/${id}`);
  return res.data;
}

export async function createPlan(input: HeadcountPlanInput): Promise<HeadcountPlan> {
  const res = await client.post("/workforce/plans", input);
  return res.data;
}

export async function updatePlan(
  id: string,
  input: Partial<HeadcountPlanInput>,
): Promise<HeadcountPlan> {
  const res = await client.patch(`/workforce/plans/${id}`, input);
  return res.data;
}

export async function deletePlan(id: string): Promise<void> {
  await client.delete(`/workforce/plans/${id}`);
}

export async function addHire(
  planId: string,
  input: PlannedHireInput,
): Promise<PlannedHire> {
  const res = await client.post(`/workforce/plans/${planId}/hires`, input);
  return res.data;
}

export async function updateHire(
  hireId: string,
  input: PlannedHireUpdate,
): Promise<PlannedHire> {
  const res = await client.patch(`/workforce/hires/${hireId}`, input);
  return res.data;
}

export async function deleteHire(hireId: string): Promise<void> {
  await client.delete(`/workforce/hires/${hireId}`);
}

export async function fetchImpact(planId: string): Promise<ImpactProjection> {
  const res = await client.get(`/workforce/plans/${planId}/impact`);
  return res.data;
}

export async function runScenario(
  input: ScenarioRequest,
): Promise<ScenarioResponse> {
  const res = await client.post("/workforce/scenario", input);
  return res.data;
}
